import { useState, useEffect } from "react";
import { Dialog } from "../../../../components/common/dialog";
import { Input } from "../../../../components/common/input";
import { Text } from "../../../../components/common/typography";
import type { ProductVariantDto } from "../../../../api";
import { formatDisplayName } from "../../utils/format-display-name";

interface UpdateStockDialogProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  variant: ProductVariantDto | null;
  currentStock: number;
  onUpdate: (variantId: number, newStock: number) => Promise<void>;
}

export function UpdateStockDialog({
  open,
  setOpen,
  variant,
  currentStock,
  onUpdate,
}: UpdateStockDialogProps) {
  const [stockValue, setStockValue] = useState<string>(currentStock.toString());
  const [isUpdating, setIsUpdating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Reset form when dialog opens or variant changes
  useEffect(() => {
    if (open && variant) {
      setStockValue(currentStock.toString());
      setError(null);
    }
  }, [open, variant, currentStock]);

  const handleSubmit = async () => {
    if (!variant || isUpdating) return;

    const stockNum = parseInt(stockValue, 10);

    if (isNaN(stockNum) || stockNum < 0) {
      setError("Please enter a valid number (0 or greater)");
      return;
    }

    setIsUpdating(true);
    setError(null);

    try {
      await onUpdate(variant.id, stockNum);
      setOpen(false);
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : "Failed to update stock. Please try again."
      );
    } finally {
      setIsUpdating(false);
    }
  };

  const handleClose = () => {
    if (!isUpdating) {
      setOpen(false);
      setError(null);
      setStockValue(currentStock.toString());
    }
  };

  return (
    <Dialog
      open={open}
      setOpen={handleClose}
      title="Update Stock"
      showCloseButton={true}
      showButtons={true}
      onConfirm={handleSubmit}
      buttonText={isUpdating ? "Updating..." : "Update Stock"}
      cancelText="Cancel"
      width="max-w-md"
      content={
        <div className="space-y-4">
          {variant && (
            <div>
              <Text variant="body" tone="body" className="mb-2">
                Variant:{" "}
                {formatDisplayName(variant.displayName) ||
                  variant.name ||
                  "Variant"}
              </Text>
              {variant.defaultCode && (
                <Text variant="bodyXs" tone="body">
                  SKU: {variant.defaultCode}
                </Text>
              )}
            </div>
          )}

          <Input
            label="Stock Quantity"
            type="number"
            name="stock"
            value={stockValue}
            onChange={(e) => {
              setStockValue(e.target.value);
              setError(null);
            }}
            placeholder="Enter stock quantity"
            required
           
            error={error ?? undefined}
            min={0}
          />
        </div>
      }
    />
  );
}
